import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

// Form state for the editor dialogs.
//
// react-hook-form is hooks-only and DialogAbstract is a class that is also
// subclassed (UpdateCommissionDialog), so the hook lives here and the class
// consumes it through a render prop rather than being converted.
//
// This also supplies the real <form> element the dialogs never had, which is what
// makes Enter submit.

const EditorForm = ({ defaultValues, resetKey, onSubmit, children }) => {
    const form = useForm({
        defaultValues,
        // formState.isValid only tracks in RHF v7 when the mode is onChange,
        // onTouched or all. onTouched is the one that also behaves well: a field
        // is not marked wrong while it is first being filled in, and from then on
        // the error clears as soon as the value becomes valid.
        mode: 'onTouched'
    });

    // The dialog normally unmounts between openings, so defaultValues arrive
    // fresh. This covers the case where it stays mounted and the record changes
    // underneath it — useForm captures defaultValues once and would otherwise
    // keep showing the previous record.
    useEffect(() => {
        form.reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);

    return (
        // The form sits between the dialog's Paper and its title, content and
        // actions. As a plain block it broke the Paper's column, and a long
        // form scrolled as one piece: the title went off the top and Save was
        // reachable only at the bottom. As a flex column that can shrink, the
        // content scrolls between a fixed title and footer, as MUI intends.
        <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                flex: '1 1 auto'
            }}
        >
            {children(form)}
        </form>
    );
};

EditorForm.propTypes = {
    /** Initial values, as produced by GridDataAdapter.transform. */
    defaultValues: PropTypes.object,
    /** Changing this re-seeds the form from defaultValues. */
    resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Receives validated values; only called when the form passes validation. */
    onSubmit: PropTypes.func.isRequired,
    /** Render prop, receives the react-hook-form API. */
    children: PropTypes.func.isRequired
};

export default EditorForm;
